import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { TrendingUp, CheckCircle, Lock, User } from 'lucide-react';
import { ThemeToggle } from '../components/ThemeToggle';
import { toast } from 'sonner';
import axios from 'axios';
import GlobalHeader from '../components/GlobalHeader';
import GlobalFooter from '../components/GlobalFooter';
import PageSEO from '../components/PageSEO';
export default function PaymentPassword({ onLogin }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const email = searchParams.get('email') || '';
  const plan = searchParams.get('plan') || 'monitor';
  const upgraded = searchParams.get('upgraded') === 'true';

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Build a payment_intent token from query params to authenticate
  // We encode email + plan in a JWT via the backend
  const [setupToken, setSetupToken] = useState(null);
  const [tokenLoading, setTokenLoading] = useState(true);

  useEffect(() => {
    if (!email) {
      toast.error('Invalid access. Redirecting to pricing...');
      setTimeout(() => navigate('/pricing'), 2000);
      return;
    }
    // Request a setup token from the backend by providing the email in the upgraded flow
    axios.post('/payment/setup-token', { email, plan })
      .then(res => setSetupToken(res.data.setup_token))
      .catch(() => {
        // If that fails, just pass email/plan directly to set-password 
        // (server will handle it with direct email + plan payload)
        setSetupToken('direct');
      })
      .finally(() => setTokenLoading(false));
  }, [email, plan, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) { toast.error('Please enter your full name'); return; }
    if (password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }

    setLoading(true);
    try {
      let res;
      if (setupToken && setupToken !== 'direct') {
        res = await axios.post('/payment/set-password', {
          token: setupToken,
          name: name.trim(),
          password
        });
      } else {
        // Fallback: use direct registration with premium flag from Stripe metadata
        res = await axios.post('/payment/complete-registration', {
          email,
          plan,
          name: name.trim(),
          password
        });
      }
      toast.success('Account created! Welcome to AI Search Advisers 🎉');
      if (onLogin && res.data.token && res.data.tenant) {
        onLogin(res.data.token, res.data.tenant);
      }
      navigate('/dashboard');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to complete setup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (tokenLoading) {
    return (
      <div className="min-h-screen bg-white dark:bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#021426] border-t-[#FEAB3D] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Setting up your account...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-background transition-colors flex flex-col">
      <PageSEO title="Setup Account | AI Search Advisers" isPrivate={true} />
      <GlobalHeader />

      <div className="flex-grow flex items-center justify-center px-6 pt-28 pb-16">
        <div className="w-full max-w-md">

          {/* Success badge */}
          {upgraded && (
            <div className="flex justify-center mb-8">
              <div className="inline-flex items-center gap-3 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-2xl px-6 py-4 animate-in fade-in slide-in-from-top-2 duration-500">
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                <div>
                  <p className="font-bold text-green-800 dark:text-green-300">Payment Successful!</p>
                  <p className="text-xs text-green-600 dark:text-green-400">Now let's set up your account to get started.</p>
                </div>
              </div>
            </div>
          )}

          {/* Card */}
          <div className="bg-white dark:bg-card rounded-3xl border border-slate-200 dark:border-border shadow-xl p-8">
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Set up your account</h1>
              <p className="text-slate-500 dark:text-slate-400 text-sm">
                One last step — create your password to access your new{' '}
                <span className="font-semibold text-[#021426] dark:text-[#FEAB3D] capitalize">{plan} Plan</span>{' '}
                dashboard.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email (read-only) */}
              <div>
                <Label className="text-slate-700 dark:text-slate-300 mb-1.5 block">Email</Label>
                <Input
                  type="email"
                  value={email}
                  disabled
                  className="h-11 bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 cursor-not-allowed"
                />
              </div>

              {/* Name */}
              <div>
                <Label htmlFor="name" className="text-slate-700 dark:text-slate-300 mb-1.5 block">
                  <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> Full Name</span>
                </Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  required
                  className="h-11"
                />
              </div>

              {/* Password */}
              <div>
                <Label htmlFor="password" className="text-slate-700 dark:text-slate-300 mb-1.5 block">
                  <span className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Password</span>
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Min. 8 characters"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  className="h-11"
                />
                {password && (
                  <div className="mt-2 flex gap-1">
                    {[1,2,3,4].map(i => (
                      <div key={i} className={`h-1 flex-1 rounded-full transition-all ${password.length >= i * 2 ? (password.length >= 8 ? 'bg-green-400' : 'bg-yellow-400') : 'bg-slate-200 dark:bg-slate-700'}`} />
                    ))}
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <Label htmlFor="confirmPassword" className="text-slate-700 dark:text-slate-300 mb-1.5 block">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Repeat your password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  required
                  className={`h-11 ${confirmPassword && confirmPassword !== password ? 'border-red-400 dark:border-red-500' : ''}`}
                />
                {confirmPassword && confirmPassword !== password && (
                  <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading || !name || password.length < 8 || password !== confirmPassword}
                className="w-full h-12 bg-[#FEAB3D] text-[#021426] hover:bg-[#e8960a] font-bold text-base rounded-none mt-2"
              >
                {loading ? 'Creating account...' : 'Complete Setup & Go to Dashboard →'}
              </Button>
            </form>
          </div>

          <p className="text-center text-xs text-slate-400 dark:text-slate-500 mt-6">
            Already have an account?{' '}
            <button onClick={() => navigate('/auth')} className="text-[#021426] dark:text-[#FEAB3D] font-semibold hover:underline">
              Sign in
            </button>
          </p>
        </div>
      </div>
      <GlobalFooter />
    </div>
  );
}
